// ... existing code ...
import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { paymentService } from '../../../services/paymentService';
import { useAuth } from '../../../contexts/AuthContext';

const TransactionHistoryPanel = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    loadOrders();
    const unsubscribe = paymentService?.subscribeToOrders(() => {
      loadOrders();
    });
    return () => unsubscribe?.();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await paymentService?.getAllOrders();
      setOrders(data || []);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders?.filter(order => {
    if (filter === 'all') return true;
    if (filter === 'pending') return order?.paymentStatus === 'pending';
    if (filter === 'succeeded') return order?.paymentStatus === 'succeeded';
    if (filter === 'failed') return order?.paymentStatus === 'failed';
    return true;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded':
        return 'text-success bg-success/10';
      case 'pending':
        return 'text-warning bg-warning/10';
      case 'failed': case'cancelled':
        return 'text-destructive bg-destructive/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getOrderTypeLabel = (type) => {
    switch (type) {
      case 'vehicle_booking':
        return 'Location de Véhicule';
      case 'property_rental':
        return 'Location Immobilière';
      case 'service_quote':
        return 'Devis de Service';
      default:
        return type;
    }
  };

  const getOrderTypeIcon = (type) => {
    switch (type) {
      case 'vehicle_booking':
        return 'Car';
      case 'property_rental':
        return 'Home';
      case 'service_quote':
        return 'Briefcase';
      default:
        return 'ShoppingCart';
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl shadow-premium p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/4"></div>
          <div className="h-20 bg-muted rounded"></div>
          <div className="h-20 bg-muted rounded"></div>
          <div className="h-20 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-premium">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Historique des Transactions</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Suivez tous les paiements et commandes
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={loadOrders}
          >
            Actualiser
          </Button>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'all' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Tous ({orders?.length || 0})
          </button>
          <button
            onClick={() => setFilter('succeeded')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'succeeded' ?'bg-success text-success-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Réussis ({orders?.filter(o => o?.paymentStatus === 'succeeded')?.length || 0})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'pending' ?'bg-warning text-warning-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            En attente ({orders?.filter(o => o?.paymentStatus === 'pending')?.length || 0})
          </button>
          <button
            onClick={() => setFilter('failed')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'failed' ?'bg-destructive text-destructive-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Échoués ({orders?.filter(o => o?.paymentStatus === 'failed')?.length || 0})
          </button>
        </div>
      </div>

      <div className="p-6">
        {filteredOrders?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="ShoppingCart" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Aucune transaction trouvée</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredOrders?.map((order) => (
              <div
                key={order?.id}
                className="bg-muted/30 rounded-lg p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => setSelectedOrder(order)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name={getOrderTypeIcon(order?.orderType)} size={24} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-foreground">#{order?.orderNumber}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order?.paymentStatus)}`}>
                          {order?.paymentStatus === 'succeeded' ? 'Réussi' : 
                           order?.paymentStatus === 'pending' ? 'En attente' : 
                           order?.paymentStatus === 'failed' ? 'Échoué' : order?.paymentStatus}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {getOrderTypeLabel(order?.orderType)}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Icon name="User" size={14} />
                          <span>{order?.customerName}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Icon name="Calendar" size={14} />
                          <span>{new Date(order?.createdAt)?.toLocaleDateString('fr-FR')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-foreground">
                      {paymentService?.formatAmount(order?.totalAmount, order?.currency)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {order?.currency}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="bg-card rounded-xl shadow-premium-xl w-full max-w-2xl">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Détails de la Commande</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  #{selectedOrder?.orderNumber}
                </p>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="w-10 h-10 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Type de commande</p>
                  <p className="font-medium text-foreground">{getOrderTypeLabel(selectedOrder?.orderType)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Statut du paiement</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedOrder?.paymentStatus)}`}>
                    {selectedOrder?.paymentStatus === 'succeeded' ? 'Réussi' : 
                     selectedOrder?.paymentStatus === 'pending' ? 'En attente' : 
                     selectedOrder?.paymentStatus === 'failed' ? 'Échoué' : selectedOrder?.paymentStatus}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Client</p>
                  <p className="font-medium text-foreground">{selectedOrder?.customerName}</p>
                  <p className="text-sm text-muted-foreground">{selectedOrder?.customerEmail}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Date</p>
                  <p className="font-medium text-foreground">
                    {new Date(selectedOrder?.createdAt)?.toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <h3 className="font-semibold text-foreground mb-3">Articles</h3>
                <div className="space-y-2">
                  {selectedOrder?.orderItems?.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{item?.name}</span>
                      <span className="font-medium text-foreground">
                        {paymentService?.formatAmount(item?.total, selectedOrder?.currency)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <div className="flex justify-between mb-2">
                  <span className="text-muted-foreground">Sous-total</span>
                  <span className="font-medium text-foreground">
                    {paymentService?.formatAmount(selectedOrder?.subtotal, selectedOrder?.currency)}
                  </span>
                </div>
                {selectedOrder?.taxAmount > 0 && (
                  <div className="flex justify-between mb-2">
                    <span className="text-muted-foreground">Taxes</span>
                    <span className="font-medium text-foreground">
                      {paymentService?.formatAmount(selectedOrder?.taxAmount, selectedOrder?.currency)}
                    </span>
                  </div>
                )}
                {selectedOrder?.discountAmount > 0 && (
                  <div className="flex justify-between mb-2">
                    <span className="text-muted-foreground">Remise</span>
                    <span className="font-medium text-success">
                      -{paymentService?.formatAmount(selectedOrder?.discountAmount, selectedOrder?.currency)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-border">
                  <span className="font-semibold text-foreground">Total</span>
                  <span className="text-xl font-bold text-primary">
                    {paymentService?.formatAmount(selectedOrder?.totalAmount, selectedOrder?.currency)}
                  </span>
                </div>
              </div>

              {selectedOrder?.notes && (
                <div className="border-t border-border pt-4">
                  <p className="text-sm text-muted-foreground mb-1">Notes</p>
                  <p className="text-foreground">{selectedOrder?.notes}</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-border">
              <Button
                variant="default"
                fullWidth
                onClick={() => setSelectedOrder(null)}
              >
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionHistoryPanel;